package br.gov.go.pge.permanenciaweb.site.pages

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import br.gov.go.pge.permanenciaweb.shared.DirectoryPersonDto
import br.gov.go.pge.permanenciaweb.shared.ErrorDto
import br.gov.go.pge.permanenciaweb.shared.MeDto
import br.gov.go.pge.permanenciaweb.shared.RevokeStayPermitRequest
import br.gov.go.pge.permanenciaweb.shared.StayPermitDto
import br.gov.go.pge.permanenciaweb.shared.StayPermitListDto
import br.gov.go.pge.permanenciaweb.shared.StayPermitRequest
import br.gov.go.pge.permanenciaweb.shared.StayPermitResultDto
import br.gov.go.pge.permanenciaweb.site.IndustrialPalette
import br.gov.go.pge.permanenciaweb.site.components.DateRange
import br.gov.go.pge.permanenciaweb.site.components.DateRangePicker
import br.gov.go.pge.permanenciaweb.site.components.DefaultButtonVariant
import br.gov.go.pge.permanenciaweb.site.components.DangerButtonVariant
import br.gov.go.pge.permanenciaweb.site.components.LinkButtonVariant
import br.gov.go.pge.permanenciaweb.site.components.PageHeader
import br.gov.go.pge.permanenciaweb.site.components.Panel
import br.gov.go.pge.permanenciaweb.site.components.PanelEmptyStyle
import br.gov.go.pge.permanenciaweb.site.components.PanelTitle
import br.gov.go.pge.permanenciaweb.site.components.PeopleField
import br.gov.go.pge.permanenciaweb.site.components.PersonPickerDialog
import br.gov.go.pge.permanenciaweb.site.components.PrimaryButtonVariant
import br.gov.go.pge.permanenciaweb.site.components.TableCellStyle
import br.gov.go.pge.permanenciaweb.site.components.TableHeaderCellStyle
import br.gov.go.pge.permanenciaweb.site.components.TableRowHoverStyle
import br.gov.go.pge.permanenciaweb.site.components.TableStyle
import br.gov.go.pge.permanenciaweb.site.components.Toast
import br.gov.go.pge.permanenciaweb.site.components.CivilDate
import com.varabyte.kobweb.browser.http.bodyAsBytes
import com.varabyte.kobweb.browser.http.bodyOf
import com.varabyte.kobweb.browser.http.http
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.foundation.layout.Spacer
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.display
import com.varabyte.kobweb.compose.ui.modifiers.flexDirection
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.id
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.maxWidth
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.compose.css.autoLength
import com.varabyte.kobweb.core.AppGlobals
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.isExporting
import com.varabyte.kobweb.silk.components.forms.Button
import com.varabyte.kobweb.silk.components.forms.ButtonSize
import com.varabyte.kobweb.silk.components.forms.InputSize
import com.varabyte.kobweb.silk.components.forms.TextInput
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import kotlinx.serialization.encodeToString
import kotlinx.serialization.json.Json
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.FlexDirection
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.dom.Label
import org.jetbrains.compose.web.dom.Main
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Table
import org.jetbrains.compose.web.dom.Tbody
import org.jetbrains.compose.web.dom.Td
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Th
import org.jetbrains.compose.web.dom.Thead
import org.jetbrains.compose.web.dom.Tr
import org.w3c.fetch.Response

/*
 * "Liberações": the after-hours stay form plus the list of permits.
 *
 * Access: LEADERSHIP (and ADMIN). A pre-created leadership account lands here after login and
 * stays here until an admin releases the roster for it.
 *
 * The form is deliberately structured: people come from the pushed directory (never typed by
 * hand), the period is a bounded range picker and the time window has the building-policy default
 * (19:00 → 07:00). Submitting creates one immutable row per person per day; a (person, day) that
 * already has a covering permit is reported instead of silently duplicated.
 */

private val json = Json { ignoreUnknownKeys = true }

/**
 * The per-day time window is already modelled end to end (columns, DTO, overlap rules) because it
 * is the groundwork for future windows, but it is **not** exposed yet: the form always files the
 * building-policy default (19:00 → 07:00) and the UI stays free of free-text time fields.
 */
internal const val SHOW_TIME_WINDOW_FIELD = false

val StayMainStyle = CssStyle.base {
    Modifier
        .maxWidth(64.cssRem)
        .padding(top = 1.cssRem, right = 1.5.cssRem, bottom = 1.5.cssRem, left = 1.5.cssRem)
        .display(DisplayStyle.Flex)
        .flexDirection(FlexDirection.Column)
        .margin(left = autoLength, right = autoLength)
        .gap(1.cssRem)
}

val StayFieldStyle = CssStyle.base {
    Modifier.width(100.percent).display(DisplayStyle.Flex).flexDirection(FlexDirection.Column).gap(0.25.cssRem)
}

val StayHintStyle = CssStyle.base {
    Modifier.color(IndustrialPalette.Muted).fontSize(0.75.cssRem)
}

internal class StayPageState {
    var me by mutableStateOf<MeDto?>(null)
    var loaded by mutableStateOf(false)
    var error by mutableStateOf("")

    var selected by mutableStateOf<List<DirectoryPersonDto>>(emptyList())
    var range by mutableStateOf(DateRange())
    var horaInicio by mutableStateOf("19:00")
    var horaFim by mutableStateOf("07:00")
    var note by mutableStateOf("")
    var saving by mutableStateOf(false)

    var list by mutableStateOf<List<StayPermitDto>>(emptyList())
    var showAll by mutableStateOf(false)

    var pickerOpen by mutableStateOf(false)
    var pickerQuery by mutableStateOf("")
    var pickerResults by mutableStateOf<List<DirectoryPersonDto>>(emptyList())
    var pickerLoading by mutableStateOf(false)
    var pickerError by mutableStateOf("")
    /** Last query that produced [pickerResults]; lets a reopen reuse the rows instead of blinking. */
    var pickerLastQuery by mutableStateOf<String?>(null)
    /** Non-null while the dialog is replacing this person (pinned first, "Trocar" buttons). */
    var swapTarget by mutableStateOf<DirectoryPersonDto?>(null)

    var toastMessage by mutableStateOf("")
    var toastVisible by mutableStateOf(false)
    var toastDanger by mutableStateOf(false)
    var toastSeq by mutableStateOf(0)

    val canCreate: Boolean get() = me?.canCreateStayPermits == true
    val canViewAll: Boolean get() = me?.canViewAllStayPermits == true
    val canSubmit: Boolean get() = selected.isNotEmpty() && range.isComplete && !saving

    fun toast(message: String, danger: Boolean = false) {
        toastMessage = message
        toastDanger = danger
        toastSeq++
    }

    fun openAdd() {
        swapTarget = null
        pickerError = ""
        pickerQuery = ""
        pickerOpen = true
    }

    fun openSwap(person: DirectoryPersonDto) {
        swapTarget = person
        pickerError = ""
        pickerQuery = ""
        pickerOpen = true
    }

    fun closePicker() {
        pickerOpen = false
        swapTarget = null
        pickerError = ""
    }

    fun pick(person: DirectoryPersonDto) {
        val target = swapTarget
        selected = when {
            target == null -> if (selected.any { it.idpessoa == person.idpessoa }) selected else selected + person
            else -> selected.map { if (it.idpessoa == target.idpessoa) person else it }
        }
        closePicker()
    }

    fun remove(person: DirectoryPersonDto) {
        selected = selected.filterNot { it.idpessoa == person.idpessoa }
    }

}

@Page
@Composable
fun Liberacoes() {
    val state = remember { StayPageState() }
    val scope = rememberCoroutineScope()

    LaunchedEffect(Unit) {
        document.title = "Liberações — Permanência no Prédio"
        if (AppGlobals.isExporting) return@LaunchedEffect
        state.load()
    }

    // Debounced server-side search while the picker is open.
    val pickerQuery = state.pickerQuery
    val pickerOpen = state.pickerOpen
    LaunchedEffect(pickerQuery, pickerOpen) {
        if (!pickerOpen) return@LaunchedEffect
        if (pickerQuery == state.pickerLastQuery && state.pickerResults.isNotEmpty()) return@LaunchedEffect
        // Mark loading before the debounce so the spinner covers the whole wait, not just the
        // request (a fast local server would otherwise never show it).
        state.pickerLoading = true
        if (pickerQuery.isNotBlank()) delay(300)
        state.search(pickerQuery)
    }

    val toastSeq = state.toastSeq
    LaunchedEffect(toastSeq) {
        if (toastSeq == 0) return@LaunchedEffect
        state.toastVisible = true
        delay(5000)
        state.toastVisible = false
    }

    StayPage(state) { action -> scope.launch { action() } }
}

@Composable
private fun StayPage(state: StayPageState, launchAction: ((suspend () -> Unit) -> Unit)) {
    Main(attrs = StayMainStyle.toAttrs()) {
        PageHeader(
            title = "Liberações",
            subtitle = "Pessoas autorizadas a permanecer no prédio fora do horário padrão.",
            me = state.me,
            currentPath = "/liberacoes",
        )

        if (state.error.isNotEmpty()) {
            P(attrs = Modifier.color(IndustrialPalette.Red).margin(0.cssRem).toAttrs()) { Text(state.error) }
        }

        if (state.canCreate) {
        Panel {
            PanelTitle { Text("Nova liberação") }
            Column(modifier = Modifier.padding(1.cssRem).gap(0.75.cssRem)) {
                Column(modifier = StayFieldStyle.toModifier()) {
                    Label(attrs = { attr("for", "people-add") }) { Text("Pessoas") }
                    PeopleField(
                        people = state.selected,
                        onAdd = { state.openAdd() },
                        onSwap = { state.openSwap(it) },
                        onRemove = { state.remove(it) },
                    )
                }

                Row(modifier = Modifier.gap(1.cssRem).width(100.percent)) {
                    Column(modifier = StayFieldStyle.toModifier()) {
                        Label { Text("Período") }
                        DateRangePicker(
                            range = state.range,
                            onRangeChange = { state.range = it },
                            id = "stay-date-range",
                        )
                        P(attrs = StayHintStyle.toAttrs()) {
                            Text(
                                when {
                                    state.range.isComplete -> "${state.range.size} dia(s) selecionado(s)."
                                    else -> "Selecione o primeiro e o último dia (máximo 7)."
                                }
                            )
                        }
                    }

                    if (SHOW_TIME_WINDOW_FIELD) {
                        Column(modifier = StayFieldStyle.toModifier()) {
                            Label { Text("Horário") }
                            Row(modifier = Modifier.gap(0.375.cssRem), verticalAlignment = Alignment.CenterVertically) {
                                TextInput(
                                    text = state.horaInicio,
                                    onTextChange = { state.horaInicio = it },
                                    modifier = Modifier.width(4.5.cssRem).then(Modifier.id("stay-start")),
                                    placeholder = "19:00",
                                    size = InputSize.SM,
                                )
                                Text("até")
                                TextInput(
                                    text = state.horaFim,
                                    onTextChange = { state.horaFim = it },
                                    modifier = Modifier.width(4.5.cssRem).then(Modifier.id("stay-end")),
                                    placeholder = "07:00",
                                    size = InputSize.SM,
                                )
                            }
                            P(attrs = StayHintStyle.toAttrs()) {
                                Text("Padrão: 19:00 até 07:00 do dia seguinte.")
                            }
                        }
                    }
                }

                Column(modifier = StayFieldStyle.toModifier()) {
                    Label { Text("Observação (opcional)") }
                    TextInput(
                        text = state.note,
                        onTextChange = { state.note = it },
                        modifier = Modifier.width(100.percent).then(Modifier.id("stay-note")),
                        placeholder = "ex.: força-tarefa de fechamento",
                        size = InputSize.SM,
                    )
                }

                Row(modifier = Modifier.gap(0.5.cssRem), verticalAlignment = Alignment.CenterVertically) {
                    Button(
                        onClick = { launchAction { state.submit() } },
                        variant = PrimaryButtonVariant,
                        size = ButtonSize.SM,
                        enabled = state.canSubmit,
                    ) {
                        Text(if (state.saving) "Salvando…" else "Liberar")
                    }
                    P(attrs = StayHintStyle.toAttrs()) {
                        Text("Uma liberação por pessoa e por dia. Já existentes são ignoradas e informadas.")
                    }
                }
            }
            }
        }

        Panel {
            PanelTitle {
                Row(modifier = Modifier.width(100.percent), verticalAlignment = Alignment.CenterVertically) {
                    Text(if (state.canViewAll && state.showAll) "Liberações — todas" else "Liberações — minhas")
                    Spacer()
                    if (state.canViewAll) {
                        Button(
                            onClick = {
                                state.showAll = !state.showAll
                                launchAction { state.loadList() }
                            },
                            variant = LinkButtonVariant,
                            size = ButtonSize.SM,
                        ) {
                            Text(if (state.showAll) "Ver só as minhas" else "Ver todas")
                        }
                    }
                }
            }
            when {
                !state.loaded -> P(attrs = PanelEmptyStyle.toAttrs()) { Text("Carregando…") }
                state.list.isEmpty() -> P(attrs = PanelEmptyStyle.toAttrs()) {
                    Text("Nenhuma liberação registrada.")
                }

                else -> StayTable(state.list) { id -> launchAction { state.revoke(id) } }
            }
        }

        PersonPickerDialog(
            open = state.pickerOpen,
            query = state.pickerQuery,
            onQueryChange = { state.pickerQuery = it },
            results = state.pickerResults,
            loading = state.pickerLoading,
            selectedIds = state.selected.map { it.idpessoa }.toSet(),
            pinned = state.swapTarget,
            swapMode = state.swapTarget != null,
            error = state.pickerError,
            onPick = { state.pick(it) },
            onClose = { state.closePicker() },
        )

        Toast(state.toastMessage, state.toastVisible, state.toastDanger)
    }
}

@Composable
private fun StayTable(rows: List<StayPermitDto>, onRevoke: (Long) -> Unit) {
    Table(attrs = TableStyle.toAttrs()) {
        Thead {
            Tr {
                Th(attrs = TableHeaderCellStyle.toAttrs()) { Text("Pessoa") }
                Th(attrs = TableHeaderCellStyle.toAttrs()) { Text("Dia") }
                Th(attrs = TableHeaderCellStyle.toAttrs()) { Text("Sala / Andar") }
                Th(attrs = TableHeaderCellStyle.toAttrs()) { Text("Solicitado por") }
                Th(attrs = TableHeaderCellStyle.toAttrs()) { Text("Observação") }
                Th(attrs = TableHeaderCellStyle.toAttrs()) { }
            }
        }
        Tbody {
            for (row in rows) {
                Tr(attrs = TableRowHoverStyle.toAttrs()) {
                    Td(attrs = TableCellStyle.toAttrs()) {
                        Text(row.nome)
                        if (row.revokedAt != null) {
                            P(attrs = StayHintStyle.toModifier().margin(0.cssRem).toAttrs()) {
                                Text("revogada em ${row.revokedAt}")
                            }
                        }
                    }
                    Td(attrs = TableCellStyle.toAttrs()) { Text(dayLabel(row.dia)) }
                    Td(attrs = TableCellStyle.toAttrs()) {
                        Text(listOfNotNull(row.sala, row.andar).joinToString(" · "))
                    }
                    Td(attrs = TableCellStyle.toAttrs()) { Text(row.createdBy) }
                    Td(attrs = TableCellStyle.toAttrs()) { Text(row.note.orEmpty()) }
                    Td(attrs = TableCellStyle.toAttrs()) {
                        if (row.canRevoke) {
                            Button(
                                onClick = { onRevoke(row.id) },
                                variant = DangerButtonVariant,
                                size = ButtonSize.SM,
                            ) {
                                Text("Revogar")
                            }
                        }
                    }
                }
            }
        }
    }
}

/** `2026-10-07` → `07/10/2026`; keeps the wire format as the source of truth. */
private fun dayLabel(iso: String): String {
    val date = CivilDate.parseOrNull(iso) ?: return iso
    return date.br
}

// ------------------------------------------------------------------ actions

private suspend fun StayPageState.load() {
    val meResponse = runCatching { window.http.get("/api/me") }.getOrNull() ?: return
    if (meResponse.status.toInt() == 401 || meResponse.redirected) return redirectToLogin()
    val me = runCatching {
        json.decodeFromString<MeDto>(meResponse.bodyAsBytes().decodeToString())
    }.getOrNull() ?: return
    this.me = me
    if (!me.canCreateStayPermits && !me.canViewStayPermits) {
        window.location.href = if (me.canViewReport) "/" else "/sem-acesso"
        return
    }
    loaded = true
    loadList()
    // Warm the picker so opening it shows rows immediately (no empty-state flash).
    // A read-only auditor (VIEW without CREATE) cannot search the directory.
    if (me.canCreateStayPermits) search("")
}

internal suspend fun StayPageState.loadList() {
    val scope = if (canViewAll && showAll) "?scope=all" else ""
    val response = runCatching { window.http.get("/api/stay-permits$scope") }.getOrNull() ?: return
    if (response.status.toInt() == 401 || response.redirected) return redirectToLogin()
    if (!response.ok) {
        error = errorMessage(response)
        return
    }
    val body = runCatching {
        json.decodeFromString<StayPermitListDto>(response.bodyAsBytes().decodeToString())
    }.getOrNull() ?: return
    list = body.items
    error = ""
}

internal suspend fun StayPageState.search(query: String) {
    pickerLoading = true
    val encoded = encodeURIComponent(query)
    val response = runCatching { window.http.get("/api/people?q=$encoded") }.getOrNull()
    pickerLoading = false
    if (response == null) {
        pickerError = "Não foi possível buscar as pessoas."
        return
    }
    if (response.status.toInt() == 401 || response.redirected) return redirectToLogin()
    if (response.status.toInt() == 403) {
        window.location.href = "/sem-acesso"
        return
    }
    if (!response.ok) {
        pickerError = errorMessage(response)
        pickerResults = emptyList()
        return
    }
    pickerResults = runCatching {
        json.decodeFromString<List<DirectoryPersonDto>>(response.bodyAsBytes().decodeToString())
    }.getOrDefault(emptyList())
    pickerLastQuery = query
    pickerError = if (pickerResults.isEmpty() && query.isNotBlank()) "Nenhuma pessoa encontrada." else ""
}

internal suspend fun StayPageState.submit() {
    val range = range
    if (selected.isEmpty() || !range.isComplete || saving) return
    saving = true
    val request = StayPermitRequest(
        idpessoas = selected.map { it.idpessoa },
        inicio = range.start!!.iso,
        fim = range.end!!.iso,
        horaInicio = horaInicio.trim().ifEmpty { null },
        horaFim = horaFim.trim().ifEmpty { null },
        note = note.trim().ifEmpty { null },
    )
    val response = runCatching {
        window.http.post(
            "/api/stay-permits",
            body = bodyOf(json.encodeToString(request), "application/json"),
        )
    }.getOrNull()
    saving = false

    if (response == null) {
        toast("Não foi possível salvar. Tente novamente.", danger = true)
        return
    }
    if (response.status.toInt() == 401 || response.redirected) return redirectToLogin()
    if (!response.ok) {
        toast(errorMessage(response), danger = true)
        return
    }

    val result = runCatching {
        json.decodeFromString<StayPermitResultDto>(response.bodyAsBytes().decodeToString())
    }.getOrNull() ?: return

    val conflicts = result.conflicts.size
    if (result.created > 0) {
        toast(
            buildString {
                append("${result.created} liberação(ões) criada(s).")
                if (conflicts > 0) append(" $conflicts já existiam e foram ignoradas.")
            },
            danger = false,
        )
        selected = emptyList()
        note = ""
    } else {
        toast(
            "Nenhuma liberação criada: $conflicts já existiam para as datas escolhidas.",
            danger = true,
        )
    }
    loadList()
}

internal suspend fun StayPageState.revoke(id: Long) {
    val response = runCatching {
        window.http.post(
            "/api/stay-permits/$id/revoke",
            body = bodyOf(json.encodeToString(RevokeStayPermitRequest()), "application/json"),
        )
    }.getOrNull() ?: return
    if (response.status.toInt() == 401 || response.redirected) return redirectToLogin()
    if (!response.ok) {
        toast(errorMessage(response), danger = true)
        return
    }
    toast("Liberação revogada.")
    loadList()
}

private fun redirectToLogin() {
    window.location.href = "/login"
}

private suspend fun errorMessage(response: Response): String = try {
    json.decodeFromString<ErrorDto>(response.bodyAsBytes(requireOk = false).decodeToString()).error
} catch (t: Throwable) {
    "erro ${response.status}"
}

private external fun encodeURIComponent(value: String): String
